import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useWeekEvents } from '@/hooks/useWeekEvents';
import { deadlineNear, isPast } from '@/lib/calendarItems';
import { cn } from '@/lib/utils';

/**
 * Календарь недели в правой колонке главной (макет «Desktop Web v2»):
 * месяц, семь дней с точками занятий и строка ближайшего занятия.
 */
export function WeekCard() {
  const { t, i18n } = useTranslation();
  const { today, days, eventsOf, picked, setPicked, shownDay, upcomingList } = useWeekEvents();

  const month = today.toLocaleDateString(i18n.language, { month: 'long' });

  return (
    <div className="rounded-[20px] bg-surface-container px-[18px] py-[15px]">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-[13.5px] font-bold capitalize text-on-surface">{month}</div>
        <Link to="/app/schedule" className="text-[12.5px] font-semibold text-info hover:underline">
          {t('dashboard.scheduleLink')}
        </Link>
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {days.map((d) => {
          const isToday = d.toDateString() === today.toDateString();
          const isShown = d.toDateString() === shownDay.toDateString();
          const has = eventsOf(d).length > 0;
          return (
            <button
              key={d.toISOString()}
              type="button"
              onClick={() => setPicked(picked && d.toDateString() === picked.toDateString() ? null : d)}
              className="flex flex-col items-center gap-[5px]"
            >
              <span className="text-[9.5px] font-semibold text-on-surface-variant">
                {d.toLocaleDateString(i18n.language, { weekday: 'short' }).replace('.', '')}
              </span>
              <span
                className={cn(
                  'grid h-[27px] w-[27px] place-items-center rounded-full text-[11.5px] font-semibold transition',
                  isShown
                    ? 'bg-primary text-on-primary'
                    : isToday
                      ? 'bg-primary-container text-on-primary-container'
                      : 'text-on-surface hover:bg-surface-container-high',
                )}
              >
                {d.getDate()}
              </span>
              {/* Точка = в этот день есть занятие. Место под неё занято всегда,
                  иначе строка дней дёргается по высоте при переключении недели. */}
              <span className={cn('h-[5px] w-[5px] rounded-full', has ? 'bg-primary' : 'bg-transparent')} />
            </button>
          );
        })}
      </div>

      <div className="mt-3 border-t border-outline pt-[11px]">
        {/* ⚠️ ДО ТРЁХ БЛИЖАЙШИХ. Одно занятие в карточке заказчик 07.09.2026
            назвал «неприкольно» — и по делу: у человека день из трёх дел, а
            видно одно. Начавшееся сегодня не прячется, а стоит ниже и приглушено:
            точка под днём и строка обязаны совпадать (02.10.2026). */}
        {upcomingList.length > 0 ? (
          <div className="flex flex-col gap-2">
            {upcomingList.map((ev) => (
              <Link
                key={ev.id}
                to="/app/schedule"
                className={cn('flex items-center gap-3', isPast(ev, today) && 'opacity-50')}
              >
                <span className="flex-none text-[11.5px] font-semibold text-on-primary-container">
                  {ev.start.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}
                </span>
                <span className="min-w-0 flex-1 truncate text-[12px] font-semibold text-on-surface">{ev.title}</span>
                {deadlineNear(ev, today) && (
                  <span className="flex-none rounded-full bg-primary-container px-2 py-0.5 text-[10px] font-bold text-on-primary-container">
                    {t('schedule.deadlineNear')}
                  </span>
                )}
                <span className="material-symbols-outlined flex-none text-[14px] text-on-surface-variant">chevron_right</span>
              </Link>
            ))}
          </div>
        ) : (
          /* Пустой день — прямая строка, а не пустота: иначе карточка
             выглядит недогруженной. */
          <p className="text-[12px] text-on-surface-variant">{t('dashboard.noPlanOnDay', { day: shownDay.toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' }) })}</p>
        )}
      </div>
    </div>
  );
}
